import { ACHIEVEMENTS, type AchievementDef, MAX_POINTS, RANKS } from '@am/protocol';
import { useEffect, useState } from 'react';
import {
  type Board,
  type BoardRow,
  type Leaderboard,
  leaderboard,
  myProfile,
  type PublicProfile,
  pickTitle,
  profileOf,
} from '../community';
import { formatMoney, type Lang, localeOf, type MessageKey, useT } from '../i18n';
import { useGame } from '../store';
import { AchievementBadge, RankEmblem } from './AchievementBadge';
import { Icon } from './icons';

/*
 * The title screen's community pages: the world leaderboards, and player profiles with
 * achievements, titles and records (one's own, with the title picker, or anyone's from the
 * leaderboard).
 */

const BOARDS: Board[] = ['world', 'value', 'rating'];
const LEADS: Record<Board, MessageKey> = {
  world: 'lb.leadWorld',
  value: 'lb.lead',
  rating: 'lb.leadRating',
};
const GROUPS: AchievementDef['group'][] = [
  'traffic',
  'business',
  'service',
  'growth',
  'build',
  'social',
];
/** Badges in a profile's showcase: the finest the player has. */
const SHOWCASE = 8;

/** A player's shown title as words. */
export function TitleChip({ title }: { title: string | null | undefined }) {
  const t = useT();
  if (!title) return null;
  return (
    <span className={`title-chip ${title.startsWith('title.') ? 'special' : ''}`}>
      {t(title as MessageKey)}
    </span>
  );
}

function score(by: Board, value: number, lang: Lang): string {
  if (by === 'value') return formatMoney(value, lang);
  if (by === 'rating') return `${(value / 200).toFixed(1)} ★`;
  return value.toLocaleString(localeOf(lang));
}

/** A row of a leaderboard: place (a medal for the first three), player, airport, score. */
function BoardItem({
  row,
  by,
  mine,
  onProfile,
}: {
  row: BoardRow;
  by: Board;
  mine: boolean;
  onProfile: (username: string) => void;
}) {
  const lang = useGame((s) => s.lang);
  return (
    <li className={mine ? 'mine' : ''}>
      <button type="button" onClick={() => onProfile(row.username)}>
        <span className={`board-rank r${Math.min(row.rank, 4)}`}>
          {row.rank <= 3 ? <Icon name="medal" size={18} /> : row.rank}
        </span>
        <span className="board-who">
          <span className="board-name">
            {row.username} <TitleChip title={row.title} />
          </span>
          <span className="muted small">{row.airportName}</span>
        </span>
        <strong className="board-score">{score(by, row.score, lang)}</strong>
      </button>
    </li>
  );
}

export function LeaderboardPage({ onProfile }: { onProfile: (username: string) => void }) {
  const t = useT();
  const me = useGame((s) => s.account?.username);
  const [by, setBy] = useState<Board>('world');
  const [board, setBoard] = useState<Leaderboard | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setBoard(null);
    setError(false);
    leaderboard(by)
      .then((b) => alive && setBoard(b))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [by]);

  return (
    <div className="sheet-body community">
      <div className="segmented board-tabs">
        {BOARDS.map((b) => (
          <button
            key={b}
            type="button"
            className={b === by ? 'active' : ''}
            aria-pressed={b === by}
            onClick={() => setBy(b)}
          >
            {t(`lb.${b}` as MessageKey)}
          </button>
        ))}
      </div>
      <p className="muted small">{t(LEADS[by])}</p>
      {error && <p className="bad small">{t('lb.error')}</p>}
      {!board && !error && <p className="muted small">{t('lb.loading')}</p>}
      {board && (
        <>
          <p className="board-me small">
            {board.me ? t('lb.you', { rank: board.me.rank, n: board.players }) : t('lb.notYet')}
          </p>
          {board.entries.length === 0 ? (
            <p className="muted">{t('lb.empty')}</p>
          ) : (
            <ol className="board-list">
              {board.entries.map((row) => (
                <BoardItem
                  key={row.username}
                  row={row}
                  by={by}
                  mine={row.username === me}
                  onProfile={onProfile}
                />
              ))}
              {/* Further down: who is just above and below the player. */}
              {(board.around?.length ?? 0) > 0 && (
                <li className="board-gap" aria-hidden>
                  ⋯
                </li>
              )}
              {board.around?.map((row) => (
                <BoardItem
                  key={row.username}
                  row={row}
                  by={by}
                  mine={row.username === me}
                  onProfile={onProfile}
                />
              ))}
            </ol>
          )}
        </>
      )}
    </div>
  );
}

/** The player's own profile (`username` absent) or anyone's. */
export function ProfilePage({ username, onBack }: { username?: string; onBack?: () => void }) {
  const t = useT();
  const lang = useGame((s) => s.lang);
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [error, setError] = useState(false);
  const own = username === undefined;

  useEffect(() => {
    let alive = true;
    setProfile(null);
    setError(false);
    (own ? myProfile() : profileOf(username ?? ''))
      .then((p) => alive && setProfile(p))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [own, username]);

  if (error) return <p className="sheet-body bad small">{t('profile.error')}</p>;
  if (!profile) return <p className="sheet-body muted small">{t('lb.loading')}</p>;

  const unlocked = Object.keys(profile.achievements);
  const next = RANKS.find((r) => r.points > profile.points);
  const progress = Math.min(1, profile.points / MAX_POINTS);
  const date = (ms: number) => new Date(ms).toLocaleDateString(lang);
  const when = (id: string) => profile.achievements[id] ?? 0;
  // The finest first, and of those the latest.
  const showcase = ACHIEVEMENTS.filter((a) => a.id in profile.achievements)
    .sort((a, b) => b.points - a.points || when(b.id) - when(a.id))
    .slice(0, SHOWCASE);

  return (
    <div className="sheet-body community profile">
      {onBack && (
        <button type="button" className="link-btn small" onClick={onBack}>
          ← {t('profile.back')}
        </button>
      )}
      <div className="profile-head">
        <span className="profile-badge" title={t(`rank.${profile.rank}` as MessageKey)}>
          <RankEmblem key={profile.rank} rank={profile.rank} size={56} />
        </span>
        <div>
          <h3>{profile.username}</h3>
          <TitleChip title={profile.title} />
          <p className="muted small">{t('profile.joined', { date: date(profile.joined) })}</p>
        </div>
      </div>

      {showcase.length > 0 && (
        <div className="ach-showcase">
          <span className="muted small">{t('profile.showcase')}</span>
          <div className="ach-showcase-badges">
            {showcase.map((a) => (
              <span key={a.id} title={t(`ach.${a.id}` as MessageKey)}>
                <AchievementBadge id={a.id} size={44} />
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="profile-points">
        <div className="progress">
          <div style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
        <span className="small">
          {t('profile.points', { points: profile.points, max: MAX_POINTS })} ·{' '}
          {next
            ? t('profile.nextRank', {
                rank: t(`rank.${next.id}` as MessageKey),
                points: next.points,
              })
            : t('profile.maxRank')}
        </span>
      </div>

      {own && (
        <label className="field">
          <span>{t('profile.title')}</span>
          <select
            value={profile.title}
            onChange={(e) => {
              pickTitle(e.target.value)
                .then(setProfile)
                .catch(() => setError(true));
            }}
          >
            {profile.titles.map((id) => (
              <option key={id} value={id}>
                {t(id as MessageKey)}
              </option>
            ))}
          </select>
          <span className="muted small">{t('profile.titleHint')}</span>
        </label>
      )}
      {own && profile.email && (
        <p className="muted small">{t('profile.email', { email: profile.email })}</p>
      )}

      <h4 className="sheet-section">
        <Icon name="plane" size={16} />
        {t('profile.best')}
      </h4>
      <p className="small">
        {profile.best
          ? t('profile.bestLine', {
              name: profile.best.airportName,
              day: profile.best.day,
              value: formatMoney(profile.best.value, lang),
            })
          : t('profile.bestNone')}
      </p>
      <dl className="year-stats profile-records">
        <div>
          <dt className="muted small">{t('profile.bestRating')}</dt>
          <dd>{profile.bestRating > 0 ? `${(profile.bestRating / 200).toFixed(1)} ★` : '—'}</dd>
        </div>
        <div>
          <dt className="muted small">{t('profile.mostDepartures')}</dt>
          <dd>{profile.mostDepartures.toLocaleString(lang)}</dd>
        </div>
        <div>
          <dt className="muted small">{t('profile.pointsLabel')}</dt>
          <dd>{profile.points.toLocaleString(lang)}</dd>
        </div>
        {profile.world && (
          <div>
            <dt className="muted small">{t('profile.worldScore')}</dt>
            <dd>
              {profile.world.total.toLocaleString(lang)}
              {profile.worldRank ? (
                <span className="muted small">
                  {' · '}
                  {t('profile.worldRank', {
                    rank: profile.worldRank,
                    n: profile.worldPlayers ?? profile.worldRank,
                  })}
                </span>
              ) : null}
            </dd>
          </div>
        )}
      </dl>
      {profile.world && profile.world.total > 0 && (
        <p className="muted small">
          {t('profile.worldParts', {
            points: profile.world.points.toLocaleString(lang),
            value: profile.world.value.toLocaleString(lang),
            rating: profile.world.rating.toLocaleString(lang),
          })}
        </p>
      )}

      <h4 className="sheet-section">
        <Icon name="trophy" size={16} />
        {t('profile.achievements', { n: unlocked.length, total: ACHIEVEMENTS.length })}
      </h4>
      {GROUPS.map((group) => {
        const list = ACHIEVEMENTS.filter((a) => a.group === group);
        const done = list.filter((a) => a.id in profile.achievements).length;
        return (
          <div key={group} className="ach-group">
            <span className="muted small">
              {t(`achgroup.${group}` as MessageKey)} · {done}/{list.length}
            </span>
            <ul className="ach-list">
              {list.map((a) => {
                const at = profile.achievements[a.id];
                return (
                  <li
                    key={a.id}
                    className={at ? 'done' : ''}
                    title={at ? t('profile.unlocked', { date: date(at) }) : t('profile.locked')}
                  >
                    <AchievementBadge id={a.id} size={44} locked={!at} />
                    <span className="ach-text">
                      <strong>{t(`ach.${a.id}` as MessageKey)}</strong>
                      <span className="muted small">{t(`ach.${a.id}.text` as MessageKey)}</span>
                      {a.title && (
                        <span className="small ach-title">
                          <Icon name="crown" size={12} /> {t(`title.${a.title}` as MessageKey)}
                        </span>
                      )}
                    </span>
                    <span className="ach-points small">+{a.points}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
